import { faCaretLeft, faCaretRight, faClinicMedical, faRefresh, faVideo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { Button, DatePicker, Radio, Select, Space, Tooltip, message, theme } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CoreEnums from '../../core/utils/enums'
import { readBookingsAggregate } from '../Admin/bookings/utils/utils'
import { readClinics } from '../Admin/clinics/utils/utils'
import { aggregateAvailability } from '../Availability/components/utils/utils'
import { updateBooking } from '../Bookings/utils/utils'
import { updateAllBookingsState } from '../lib/booking-utils'
import { isAdmin, isDoctor, isNurse, isReception, userClinics } from '../lib/profile-utils'
import { handleError, hexToRGBA } from '../lib/utils'
import { ContainerCard } from '../reusable-components/AgiliteCards'
import CustomLoadingIcon from '../reusable-components/CustomLoadingIcon'
import DayEventContent from './full-calendar-components/DayEventContent'
import DayHeader from './full-calendar-components/DayHeader'
import FullCalendarContentModal from './full-calendar-components/FullCalendarContentModal'
import MonthEventContent from './full-calendar-components/MonthEventContent copy'
import MonthHeader from './full-calendar-components/MonthHeader'
import WeekEventContent from './full-calendar-components/WeekEventContent'
import WeekHeader from './full-calendar-components/WeekHeader'
import './full-calendar-style.css'
import { FullCalendarEnums } from './full-calendar-utils/full-calendar-enums'
import { diaryViewOptions } from './full-calendar-utils/full-calendar-templates'
import AppointmentsCalendarReducer from './utils/appointments-calendar-reducer'

const EHRCalendar = ({
  medicalProfessionals,
  medicalProfessionalsLoading,
  currentDate,
  setCurrentDate,
  currentView,
  setCurrentView,
  dateRange,
  filterActive,
  clearDateRangeFilter
}) => {
  const [isVirtualClinic, setIsVirtualClinic] = useState(false)
  const calendarRef = useRef(null)
  const clinicsState = useSelector((state) => state.clinics.data)
  const authState = useSelector((state) => state.auth)
  const servicesState = useSelector((state) => state.services.data)
  const virtualServicesState = useSelector((state) => state.virtualServices.data)
  const sideBarExpanded = useSelector((state) => state.core.sideBarExpanded)
  const dispatch = useDispatch()
  const appointmentsCalendarState = useSelector((state) => state.appointmentsCalendar.data)
  const { token } = theme.useToken()
  const [events, setEvents] = useState([]) // Initialize events state with your initial events array
  const [appointmentsLoading, setAppointmentsLoading] = useState(false)
  const [selectedProfessional, setSelectedProfessional] = useState(null)
  const [sortedMedicalProfessionals, setSortedMedicalProfessionals] = useState([])
  const dateFormat = 'YYYY-MM-DD'
  const [modalData, setModalData] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [isListView, setIsListView] = useState(false)
  const [timeRangeFilter, setTimeRangeFilter] = useState(null)
  const [timeFilterActive, setTimeFilterActive] = useState(false)
  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)
  const [facilityFilter, setFacilityFilter] = useState('')
  const [facilities, setFacilities] = useState([])

  useEffect(() => {
    // Initializes the function without a medical professional (medprof) assigned. Upon invocation, this function triggers the setting of a medical professional, which leads to its execution again, this time with a medical professional assigned. Consequently, the fetchAppointments() function is called to retrieve appointments associated with the assigned medical professional.
    if (!selectedProfessional) {
      let tmpSelectedProfessional = medicalProfessionals[0]
      if (isDoctor()) {
        setSelectedProfessional(authState.agiliteUser?._id)
      }
      if (isReception() || isAdmin()) {
        setSelectedProfessional(tmpSelectedProfessional?._id)
      }
      if (isNurse()) {
        setSelectedProfessional(tmpSelectedProfessional?._id)
      }
    } else {
      fetchAppointments()
    }
    // eslint-disable-next-line
  }, [selectedProfessional, isVirtualClinic])

  useEffect(() => {
    // Initially, no medical professional (medprof) is set, so it's essential not to call fetchAppointments().
    if (selectedProfessional) {
      fetchAppointments()
    }
    // eslint-disable-next-line
  }, [currentDate, currentView, facilityFilter])

  const bookingRequestQueries = () => {
    let tmpQry = {}

    if (filterActive && dateRange) {
      // If date range filter is active, use it regardless of view
      tmpQry.bookingDate = {
        $gte: dayjs(dateRange.start).format(dateFormat),
        $lte: dayjs(dateRange.end).format(dateFormat)
      }
    } else {
      // Standard view-based filtering
      switch (currentView) {
        case FullCalendarEnums.viewTypeKeys.DAY_VIEW:
          tmpQry.bookingDate = { $eq: dayjs(currentDate).format(dateFormat) }
          break
        case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
          tmpQry.bookingDate = {
            $gte: dayjs(currentDate).format(dateFormat),
            $lt: dayjs(currentDate).add(7, 'day').format(dateFormat)
          }
          break
        case FullCalendarEnums.viewTypeKeys.MONTH_VIEW:
          const lastDayOfPreviousMonth = dayjs(currentDate).subtract(1, 'month').endOf('month')
          // Calculate the first day of the next month
          const firstDayOfNextMonth = dayjs(currentDate).add(1, 'month').startOf('month')

          tmpQry.bookingDate = {
            $gt: lastDayOfPreviousMonth.format(dateFormat),
            $lt: firstDayOfNextMonth.format(dateFormat)
          }
          break
        default:
        // tmpQry.bookingDate = {
        //   $gte: dayjs(dateRange?.start).format('YYYY-MMM-DD'),
        //   $lt: dayjs(dateRange?.end).add(1, 'day').format('YYYY-MMM-DD')
        // }
      }
    }

    if (isReception()) {
      tmpQry.medicalProfRef = selectedProfessional
    }

    if (isDoctor()) {
      tmpQry.medicalProfRef = selectedProfessional
    }

    if (isNurse()) {
      tmpQry.clinicRef = { $in: userClinics() }
    }
    if (isAdmin()) {
      tmpQry.medicalProfRef = selectedProfessional
    }

    if (facilityFilter && facilityFilter !== '') {
      tmpQry.clinicRef = { $in: [facilityFilter] }
    }

    tmpQry.status = { $ne: CoreEnums.bookingStatuses.cancelled }
    return tmpQry
  }

  useEffect(() => {
    handleSetEvents()
    // eslint-disable-next-line
  }, [appointmentsCalendarState, isListView])

  const formatEventTime = (time, date) => {
    const bookingDate = dayjs(date).format('YYYY-MM-DD') // Booking date in YYYY-MM-DD format

    // Combine booking date and start time to create the start date-time value
    const formattedDate = new Date(`${bookingDate}T${time}`)
    // Convert date-time values to ISO 8601 format
    return formattedDate.toISOString()
  }
  const getEventColor = (status, opacity) => {
    if (!status) return hexToRGBA(token.colorError, 0.8)
    switch (status) {
      case CoreEnums.bookingStatuses.checkin:
        return hexToRGBA(token.colorPrimary, opacity)
      case CoreEnums.bookingStatuses.screening:
        return hexToRGBA(token.colorSecondary, opacity)
      case CoreEnums.bookingStatuses.consultation:
        return hexToRGBA(token.colorWarning, opacity)
      case CoreEnums.bookingStatuses.reception:
        return hexToRGBA(token.colorInfo, opacity)
      case CoreEnums.bookingStatuses.completed:
        return hexToRGBA(token.colorSuccess, opacity)
    }
  }
  const handleSetEvents = () => {
    let tmpEvents = []
    const targetMedProf = sortedMedicalProfessionals.find((medProf) => medProf._id === selectedProfessional)
    appointmentsCalendarState.forEach((appointment) => {
      tmpEvents.push({
        id: appointment._id, // Unique identifier for the event
        // Title of the event
        start: formatEventTime(appointment.startTime, appointment.bookingDate), // Start date and time of the event (in ISO 8601 format)
        end: formatEventTime(appointment.endTime, appointment.bookingDate), // End date and time of the event (in ISO 8601 format),
        recordData: appointment,
        color: getEventColor(appointment.status, isListView ? 1 : 0.2)
      })
    })
    if (!isListView && !isNurse()) {
      if (targetMedProf && targetMedProf.availability && targetMedProf.availability.length > 0) {
        targetMedProf.availability.forEach((entry, index) => {
          const validEntries = validateAvailability(entry)
          validEntries.forEach((e) => {
            tmpEvents.push({
              availabilityRecordId: entry.availabilityRecordId,
              availabilityEntryId: entry.availabilityEntryId,
              // entry id
              id: e._id, // Unique identifier for the event
              // Title of the event
              start: formatEventTime(e.time, entry.date), // Start date and time of the event (in ISO 8601 format)
              end: formatEventTime(dayjs(e.time, 'HH:mm').add(15, 'minute').format('HH:mm'), entry.date), // End date and time of the event (in ISO 8601 format),
              color: e.blocked ? hexToRGBA(token.colorError, 0.2) : hexToRGBA('#ffffff', 0.2),
              isAvailabilityEntry: true,
              editable: false,
              blocked: e.blocked,
              blockReason: e.blockReason,
              blockedBy: e.blockedBy,
              unblockedBy: e.unblockedBy
            })
          })
        })
      }
    }
    setEvents(tmpEvents)
  }

  const validateAvailability = (entry) => {
    const availabilityDate = entry.date // YYYY-MM-DD
    const availabilityTimeslots = JSON.parse(JSON.stringify(entry.timeSlots))

    let filteredSlots = availabilityTimeslots.filter((slot) => {
      const isBooked = appointmentsCalendarState.some(
        (booking) =>
          booking.startTime === slot.time &&
          dayjs(booking.bookingDate).format(dateFormat) === availabilityDate &&
          booking.status !== CoreEnums.bookingStatuses.cancelled
      )

      const hasOverlap = appointmentsCalendarState.some((booking) => {
        const bookingStartTime = dayjs(booking.startTime, 'HH:mm')
        const bookingEndTime = dayjs(booking.endTime, 'HH:mm')
        const slotTime = dayjs(slot.time, 'HH:mm')
        return (
          booking.status !== CoreEnums.bookingStatuses.cancelled &&
          dayjs(booking.bookingDate).format(dateFormat) === availabilityDate &&
          dayjs(slotTime).isAfter(bookingStartTime) &&
          dayjs(slotTime).isBefore(bookingEndTime)
        )
      })
      return !isBooked && !hasOverlap
    })
    return filteredSlots
  }

  const fetchAppointments = async () => {
    let tmpmedProfsOnDuty = []
    let tmpMedicalPofessionals = JSON.parse(JSON.stringify(medicalProfessionals))
    let tmpData = []
    let qry = {}
    setAppointmentsLoading(true)

    try {
      const pipeline = avalabilityRequestPipeline()

      if (pipeline.length === 0) {
        console.warn('⚠️ Empty pipeline returned, skipping availability aggregation')
        setSortedMedicalProfessionals(tmpMedicalPofessionals)
      } else {
        try {
          tmpmedProfsOnDuty = await aggregateAvailability(pipeline)
        } catch (availabilityError) {
          console.error('❌ Availability aggregation failed:', availabilityError)
          console.error('📋 Failed pipeline:', JSON.stringify(pipeline, null, 2))

          // Continue without availability data
          tmpmedProfsOnDuty = []
        }
      }

      // Get unique medical professional IDs from availability
      const availableIds = [...new Set(tmpmedProfsOnDuty.map((item) => item.medicalProfessional))]

      // Split professionals into available and unavailable
      const availableDoctors = tmpMedicalPofessionals.filter((prof) => availableIds.includes(prof._id))
      const unavailableDoctors = tmpMedicalPofessionals.filter((prof) => !availableIds.includes(prof._id))

      // Assign availability to each available doctor
      availableDoctors.forEach((medProf) => {
        medProf.onDuty = true
        medProf.availability = tmpmedProfsOnDuty.filter((entry) => entry.medicalProfessional === medProf._id)
      })

      const tmpSortedProfessionals = [...availableDoctors, ...unavailableDoctors]
      setSortedMedicalProfessionals(tmpSortedProfessionals)

      qry = { ...bookingRequestQueries(), adhoc: { $ne: true } }
      tmpData = await readBookingsAggregate(qry)
      console.log('Events', tmpData)
      dispatch(AppointmentsCalendarReducer.actions.setRecords(tmpData))
    } catch (e) {
      console.error('❌ General fetchAppointments error:', e)
      message.error(handleError(e))
    }
    setAppointmentsLoading(false)
  }

  const handleUpdateEvent = async (info) => {
    // Track the previous event state for potential rollback
    // let recordData = info.event.extendedProps.recordData
    let prevEventsState = JSON.parse(JSON.stringify(events))
    let newStart = info.event.start
    let newEnd = info.event.end
    let eventId = info.event.id
    let updatedEvent = null

    setAppointmentsLoading(true)
    try {
      // Update the event state to trigger rerendering in the calendar,
      // ensuring it recognizes the change in state. Without this step,
      // reverting to the previous events state might not trigger a rerender.
      const updatedEvents = events.map((event) => {
        // Update the dragged event
        if (event.id === info.event.id) {
          return {
            ...event,
            start: newStart,
            end: newEnd // Adjust end time if needed
          }
        }
        // Return the unchanged event
        return event
      })
      setEvents(updatedEvents)
      let payload = {
        bookingDate: dayjs(newStart).format(dateFormat),
        startTime: dayjs(newStart).format('HH:mm'),
        endTime: dayjs(newEnd).format('HH:mm')
      }
      updatedEvent = await updateBooking(payload, { _id: eventId })
      updateAllBookingsState(updatedEvent)
    } catch (e) {
      // Handle and display any errors
      message.error(handleError(e))
      // Rollback to the previous events state in case of error
      setEvents(prevEventsState)
    }
    setAppointmentsLoading(false)
  }

  useEffect(() => {
    if (modalData) {
      setModalOpen(true)
    } else {
      setModalOpen(false)
    }
  }, [modalData])

  const handleEventClick = (info) => {
    if (currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW) {
      setSelectedProfessional(info.event.extendedProps.medProfId)
      setCurrentView(FullCalendarEnums.viewTypeKeys.DAY_VIEW)
      setCurrentDate(dayjs(info.event.start).toDate())
    } else {
      setModalData(info.event)
    }
  }

  const handleDateChange = (action) => {
    let newDate
    if (action === 'today') {
      newDate = dayjs().toDate()
    } else {
      switch (currentView) {
        case FullCalendarEnums.viewTypeKeys.MONTH_VIEW:
          newDate =
            action === 'next'
              ? dayjs(currentDate).add(1, 'month').toDate()
              : dayjs(currentDate).subtract(1, 'month').toDate()
          break
        case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
          newDate =
            action === 'next'
              ? dayjs(currentDate).add(1, 'week').toDate()
              : dayjs(currentDate).subtract(1, 'week').toDate()
          break
        default:
          newDate =
            action === 'next'
              ? dayjs(currentDate).add(1, 'day').toDate()
              : dayjs(currentDate).subtract(1, 'day').toDate()
          break
      }
    }
    setCurrentDate(newDate)
  }

  const DayHeaderContent = (data) => {
    switch (currentView) {
      case FullCalendarEnums.viewTypeKeys.MONTH_VIEW:
        return <MonthHeader {...data} isListView={isListView} />
      case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
        return <WeekHeader {...data} setCurrentDate={setCurrentDate} setCurrentView={setCurrentView} />
      default:
        return <DayHeader {...data} />
    }
  }
  const renderEventContent = (eventInfo) => {
    if (eventInfo.event?.extendedProps?.isAvailabilityEntry)
      return (
        <Space direction='vertical' style={{ gap: 0 }}>
          <p style={{ fontSize: 12 }}>
            {eventInfo.event?.extendedProps?.blocked ? (
              <>
                Blocked by <b>{eventInfo.event?.extendedProps?.blockedBy}</b>
              </>
            ) : (
              <>
                Available{' '}
                {eventInfo.event?.extendedProps?.blockReason
                  ? `- previously blocked (Unblocked by ${
                      eventInfo.event?.extendedProps?.unblockedBy ? eventInfo.event?.extendedProps?.unblockedBy : 'n/a'
                    })`
                  : ''}
              </>
            )}
          </p>
          {eventInfo.event?.extendedProps?.blockReason && currentView === FullCalendarEnums.viewTypeKeys.DAY_VIEW ? (
            <p style={{ fontSize: 12 }}>
              <b>Reason:</b> {eventInfo.event?.extendedProps?.blockReason}
            </p>
          ) : undefined}
        </Space>
      )
    const recordData = eventInfo.event?.extendedProps?.recordData
    if (recordData) {
      const patientRecord = recordData?.patientRecord
      const service = virtualServicesState.find((serv) => serv._id === recordData?.service)
        ? { ...virtualServicesState.find((serv) => serv._id === recordData?.service), isVirtual: true }
        : undefined || servicesState.find((serv) => serv._id === recordData?.service) || 'Doctor Consultation'

      if (isListView)
        return (
          <DayEventContent
            patientRecord={patientRecord}
            recordData={recordData}
            service={service}
            isListView={isListView}
          />
        )
      switch (currentView) {
        case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
          return <WeekEventContent patientRecord={patientRecord} recordData={recordData} service={service} />
        case FullCalendarEnums.viewTypeKeys.MONTH_VIEW:
          return <MonthEventContent patientRecord={patientRecord} recordData={recordData} service={service} />
        default:
          return <DayEventContent patientRecord={patientRecord} recordData={recordData} service={service} />
      }
    }
  }
  const handleSlotLabelContent = (arg) => {
    // Customizing time slot label content and style
    return <div style={{ height: '50px', lineHeight: '50px' }}>{arg.text}</div>
  }

  const handleEventMouseEnter = (info) => {
    if (info.event?.extendedProps?.isAvailabilityEntry) {
      if (info.event?.extendedProps?.blocked) {
        info.el.style.backgroundColor = hexToRGBA(token.colorError, 0.4)
      } else {
        info.el.style.backgroundColor = hexToRGBA(token.colorPrimary, 0.4)
      }
    } else {
      if (!isListView) {
        if (currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW) {
          if (isDoctor()) {
            info.el.style.backgroundColor = hexToRGBA(token.colorSuccess, 0.4)
          } else {
            info.el.style.backgroundColor = hexToRGBA(token.colorPrimary, 0.4)
          }
        } else {
          info.el.style.backgroundColor = getEventColor(info.event.extendedProps.recordData.status, 0.4)
        }
      }
    }
    // Add more style changes as needed
  }

  const handleEventMouseLeave = (info) => {
    if (info.event?.extendedProps?.isAvailabilityEntry) {
      if (info.event?.extendedProps?.blocked) {
        info.el.style.backgroundColor = hexToRGBA(token.colorError, 0.2)
      } else {
        info.el.style.backgroundColor = hexToRGBA('#ffffff', 0.2)
      }
    } else {
      if (!isListView) {
        if (currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW) {
          if (isDoctor()) {
            info.el.style.backgroundColor = hexToRGBA(token.colorSuccess, 0.2)
          } else {
            info.el.style.backgroundColor = hexToRGBA(token.colorPrimary, 0.2)
          }
        } else {
          info.el.style.backgroundColor = getEventColor(info.event.extendedProps.recordData.status, 0.2)
        }
      }
    }
  }

  const handleEmptySlotClick = (info) => {
    setModalData(info)
    // Perform any desired action when an empty slot is clicked
  }

  const avalabilityRequestPipeline = () => {
    let pielineDateQuery = {}

    if (filterActive && dateRange) {
      // If date range filter is active, use it regardless of view
      pielineDateQuery = {
        $gte: dayjs(dateRange.start).format('YYYY-MM-DD'),
        $lte: dayjs(dateRange.end).format('YYYY-MM-DD')
      }
    } else {
      // Standard view-based pipeline query
      switch (currentView) {
        case FullCalendarEnums.viewTypeKeys.DAY_VIEW:
          pielineDateQuery = dayjs(currentDate).format('YYYY-MM-DD')
          break
        case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
          pielineDateQuery = {
            $gte: dayjs(currentDate).format('YYYY-MM-DD'),
            $lt: dayjs(currentDate).add(7, 'day').format('YYYY-MM-DD')
          }
          break
        case FullCalendarEnums.viewTypeKeys.MONTH_VIEW:
          pielineDateQuery = {
            $gte: dayjs(currentDate).startOf('month').format('YYYY-MM-DD'),
            $lt: dayjs(currentDate).endOf('month').format('YYYY-MM-DD')
          }
          break
        default:
          // Default to day view query to prevent undefined pipeline
          pielineDateQuery = dayjs(currentDate).format('YYYY-MM-DD')
          break
      }
    }

    // Get clinic IDs safely
    const clinicIds = isAdmin() ? clinicsState.map((clinic) => clinic._id) : userClinics()

    // Debug logging
    console.log('🔍 Availability Pipeline Debug:', {
      currentView,
      currentDate: dayjs(currentDate).format('YYYY-MM-DD'),
      pielineDateQuery,
      clinicIds,
      isVirtualClinic,
      clinicsStateLength: clinicsState?.length,
      userClinicsResult: userClinics()
    })

    // Ensure we have valid clinic IDs
    if (!clinicIds || clinicIds.length === 0) {
      console.error('❌ No valid clinic IDs found for availability pipeline')
      return []
    }

    const pipeline = [
      {
        $match: {
          clinicRef: { $in: clinicIds },
          isVirtual: isVirtualClinic ? true : { $ne: true }
        }
      },
      {
        $unwind: '$availability'
      },
      {
        $unwind: '$availability.timeSlots'
      },
      {
        $match: {
          'availability.date': pielineDateQuery
        }
      },
      {
        $group: {
          _id: {
            date: '$availability.date',
            medicalProfessional: '$medicalProfessional'
          },
          availabilityRecordId: { $first: '$_id' },
          availabilityEntryId: { $first: '$availability._id' },
          clinicRef: { $first: '$clinicRef' },
          profession: { $first: '$profession' },
          timeSlots: {
            $push: {
              _id: '$availability.timeSlots._id',
              time: '$availability.timeSlots.time',
              blocked: '$availability.timeSlots.blocked',
              blockReason: '$availability.timeSlots.blockReason',
              blockedBy: '$availability.timeSlots.blockedBy',
              unblockedBy: '$availability.timeSlots.unblockedBy'
            }
          }
        }
      },
      {
        $project: {
          availabilityRecordId: 1,
          availabilityEntryId: 1,
          clinicRef: 1,
          profession: 1,
          medicalProfessional: '$_id.medicalProfessional',
          date: '$_id.date',
          timeSlots: 1
        }
      }
    ]

    return pipeline
  }

  const extendedProps =
    currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW
      ? {
          eventContent: (info) => {
            return <b style={{ fontSize: 12 }}>{info.event.title}</b>
          }
        }
      : {
          dayCellContent: renderEventContent,
          eventContent: renderEventContent
        }

  const getMonthViewEvents = () => {
    const tmpEvents = []
    if (isDoctor()) {
      sortedMedicalProfessionals
        .filter((i) => i.onDuty && i._id === selectedProfessional)
        .forEach((medProf) => {
          medProf.availability.forEach((i) => {
            tmpEvents.push({
              title: 'AVAILABLE',
              start: i.date,
              end: i.date,
              color: hexToRGBA(token.colorSuccess, 0.2),
              medProfId: authState.agiliteUser._id
            })
          })
        })
    } else {
      sortedMedicalProfessionals
        .filter((i) => i.onDuty)
        .forEach((medProf) => {
          medProf.availability.forEach((i) => {
            tmpEvents.push({
              title: `DR. ${medProf.firstName.charAt(0)} ${medProf.lastName}`.toUpperCase(),
              start: i.date,
              end: i.date,
              color: hexToRGBA(token.colorPrimary, 0.2),
              medProfId: medProf._id
            })
          })
        })
    }

    return tmpEvents
  }

  const getCurrentListView = () => {
    switch (currentView) {
      case FullCalendarEnums.viewTypeKeys.DAY_VIEW:
        return 'listDay'
      case FullCalendarEnums.viewTypeKeys.WEEK_VIEW:
        return 'listWeek'
      default:
        return FullCalendarEnums.viewTypeKeys.MONTH_VIEW
    }
  }

  // Load time filter from localStorage on component mount
  useEffect(() => {
    const savedTimeFilter = localStorage.getItem('calendarTimeFilter')
    if (savedTimeFilter) {
      try {
        const { startTime: savedStartTime, endTime: savedEndTime } = JSON.parse(savedTimeFilter)
        if (savedStartTime) {
          setStartTime(dayjs(savedStartTime, 'HH:mm'))
        }
        if (savedEndTime) {
          setEndTime(dayjs(savedEndTime, 'HH:mm'))
        }

        // Apply the saved filter immediately
        if (savedStartTime || savedEndTime) {
          setTimeFilterActive(true)
          let formattedStartTime = '00:00'
          let formattedEndTime = '24:00'

          if (savedStartTime && savedEndTime) {
            formattedStartTime = savedStartTime
            formattedEndTime = savedEndTime
          } else if (savedStartTime) {
            formattedStartTime = savedStartTime
            formattedEndTime = '24:00'
          } else if (savedEndTime) {
            formattedStartTime = '00:00'
            formattedEndTime = savedEndTime
          }

          setTimeRangeFilter({
            start: formattedStartTime,
            end: formattedEndTime
          })
        }
      } catch (error) {
        console.error('Error loading time filter from localStorage:', error)
      }
    }
  }, [])

  // Watch for changes in start and end time and apply filter automatically
  useEffect(() => {
    // Apply filter when at least one time is selected
    if (startTime || endTime) {
      let formattedStartTime = '00:00'
      let formattedEndTime = '24:00'

      if (startTime && endTime) {
        // Both times selected - validate that start is before end
        if (dayjs(startTime).isAfter(dayjs(endTime))) {
          message.error('Start time must be before end time')
          return
        }
        formattedStartTime = dayjs(startTime).format('HH:mm')
        formattedEndTime = dayjs(endTime).format('HH:mm')
      } else if (startTime) {
        // Only start time selected - go from start time to end of day
        formattedStartTime = dayjs(startTime).format('HH:mm')
        formattedEndTime = '24:00'
      } else if (endTime) {
        // Only end time selected - go from start of day to end time
        formattedStartTime = '00:00'
        formattedEndTime = dayjs(endTime).format('HH:mm')
      }

      // Save to localStorage
      localStorage.setItem(
        'calendarTimeFilter',
        JSON.stringify({
          startTime: startTime ? dayjs(startTime).format('HH:mm') : null,
          endTime: endTime ? dayjs(endTime).format('HH:mm') : null
        })
      )

      // Update timeRangeFilter state
      setTimeRangeFilter({
        start: formattedStartTime,
        end: formattedEndTime
      })
      setTimeFilterActive(true)

      // Force update by directly manipulating the calendar API
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi()

          // Set the displayed time range
          calendarApi.setOption('slotMinTime', formattedStartTime + ':00')
          calendarApi.setOption('slotMaxTime', formattedEndTime + ':00')

          // Scroll to the start time
          calendarApi.scrollToTime(formattedStartTime + ':00')

          // Force a refetch of events and redraw
          calendarApi.refetchEvents()
          calendarApi.render()
        }
      }, 0)
    }
  }, [startTime, endTime])

  // Clear the time range filter
  const clearTimeRangeFilter = () => {
    setTimeFilterActive(false)
    setTimeRangeFilter(null)
    setStartTime(null)
    setEndTime(null)

    // Clear from localStorage
    localStorage.removeItem('calendarTimeFilter')

    // Reset calendar view with a timeout to ensure state updates first
    setTimeout(() => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi()
        calendarApi.setOption('slotMinTime', '00:00:00')
        calendarApi.setOption('slotMaxTime', '24:00:00')
        calendarApi.scrollToTime('14:00:00')
        calendarApi.refetchEvents()
        calendarApi.render()
      }
    }, 0)
  }

  // Update effect to refresh calendar when time filter changes
  useEffect(() => {
    if (calendarRef.current && timeFilterActive && timeRangeFilter) {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.setOption('slotMinTime', timeRangeFilter.start + ':00')
      calendarApi.setOption('slotMaxTime', timeRangeFilter.end + ':00')
      calendarApi.scrollToTime(timeRangeFilter.start + ':00')
      calendarApi.refetchEvents()
      calendarApi.render()
    } else if (calendarRef.current && !timeFilterActive) {
      // Reset to full day view when filter is cleared
      const calendarApi = calendarRef.current.getApi()
      calendarApi.setOption('slotMinTime', '00:00:00')
      calendarApi.setOption('slotMaxTime', '24:00:00')
      calendarApi.scrollToTime('14:00:00')
      calendarApi.refetchEvents()
      calendarApi.render()
    }
  }, [timeFilterActive, timeRangeFilter])

  useEffect(() => {
    const fetchFacilities = async () => {
      const facilities = await readClinics({ entityRef: { $in: authState.agiliteUser?.extraData.entities } })
      setFacilities(facilities)
    }

    fetchFacilities()
  }, [appointmentsLoading, authState.agiliteUser?.extraData.entities])

  return (
    <ContainerCard title='Calendar' bodyStyle={{ paddingTop: 0 }}>
      <Space style={{ padding: 8 }} wrap>
        <Radio.Group
          disabled={appointmentsLoading || medicalProfessionalsLoading}
          value={isVirtualClinic}
          onChange={(e) => setIsVirtualClinic(e.target.value)}
          buttonStyle='solid'
        >
          <Radio.Button value={false}>
            <FontAwesomeIcon icon={faClinicMedical} />
          </Radio.Button>
          <Radio.Button value={true}>
            <FontAwesomeIcon icon={faVideo} />
          </Radio.Button>
        </Radio.Group>
        <Button
          type='primary'
          onClick={() => {
            setModalData({ date: dayjs().toString(), dateStr: dayjs().toString() })
          }}
          style={{ background: token.actionColor, color: 'white' }}
        >
          New Appointment
        </Button>
        <Button
          style={
            dayjs(currentDate).format(dateFormat) === dayjs().format(dateFormat)
              ? { background: token.actionColor, color: 'white' }
              : {}
          }
          onClick={() => {
            handleDateChange('today')
          }}
        >
          Today
        </Button>
        <Radio.Group
          onChange={(e) => {
            setCurrentView(e.target.value)
          }}
          buttonStyle='solid'
          value={currentView}
        >
          {diaryViewOptions.map((t) => {
            return (
              <Radio.Button key={t.value} value={t.value}>
                {t.label.charAt(0)?.toUpperCase()}
              </Radio.Button>
            )
          })}
        </Radio.Group>

        {(currentView === FullCalendarEnums.viewTypeKeys.DAY_VIEW ||
          currentView === FullCalendarEnums.viewTypeKeys.WEEK_VIEW) && (
          <Space>
            <Select
              placeholder='Start Time'
              value={startTime ? dayjs(startTime).format('HH:mm') : undefined}
              onChange={(timeString) => {
                const time = dayjs(timeString, 'HH:mm')
                setStartTime(time)
                // Clear end time if new start time is after current end time
                if (time && endTime && time.isAfter(dayjs(endTime))) {
                  setEndTime(null)
                }
              }}
              style={{ width: 110 }}
              size='middle'
              options={Array.from({ length: 96 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = (i % 4) * 15
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                return {
                  label: timeString,
                  value: timeString
                }
              })}
            />
            <Select
              placeholder='End Time'
              value={endTime ? dayjs(endTime).format('HH:mm') : undefined}
              onChange={(timeString) => {
                const time = dayjs(timeString, 'HH:mm')
                setEndTime(time)
              }}
              style={{ width: 110 }}
              size='middle'
              options={Array.from({ length: 96 }, (_, i) => {
                const hour = Math.floor(i / 4)
                const minute = (i % 4) * 15
                const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                const isDisabled = startTime ? dayjs(timeString, 'HH:mm').isSameOrBefore(dayjs(startTime)) : false
                return {
                  label: timeString,
                  value: timeString,
                  disabled: isDisabled
                }
              })}
            />
          </Space>
        )}

        {timeFilterActive && currentView !== FullCalendarEnums.viewTypeKeys.MONTH_VIEW && (
          <Button onClick={clearTimeRangeFilter} style={{ background: token.colorWarning, color: 'white' }}>
            Clear Time Filter (
            {startTime && endTime
              ? `${dayjs(startTime).format('HH:mm')} - ${dayjs(endTime).format('HH:mm')}`
              : startTime
              ? `From ${dayjs(startTime).format('HH:mm')}`
              : endTime
              ? `Until ${dayjs(endTime).format('HH:mm')}`
              : `${timeRangeFilter.start} - ${timeRangeFilter.end}`}
            )
          </Button>
        )}

        {currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW || isDoctor() ? undefined : (
          <Select
            optionFilterProp='search'
            showSearch
            value={selectedProfessional}
            onSelect={(value) => {
              setSelectedProfessional(value)
            }}
            options={sortedMedicalProfessionals.map((medProf) => {
              return {
                search: `${medProf.firstName} ${medProf.lastName} 
              `,
                label: (
                  <>
                    {`${medProf.extraData.profession === 'doctor' ? 'Dr. ' : ''}${medProf.firstName
                      .charAt(0)
                      .toUpperCase()}. ${medProf.lastName} 
            `}{' '}
                    {medProf.onDuty ? <sup style={{ color: token.colorSuccess, fontSize: 8 }}>ON DUTY</sup> : undefined}
                  </>
                ),
                value: medProf._id
              }
            })}
            style={{ width: 250 }}
          />
        )}
        <Space style={{ gap: 0 }}>
          {/* Previous button */}
          <Button
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onClick={() => {
              handleDateChange('prev')
            }}
          >
            <FontAwesomeIcon icon={faCaretLeft} />
          </Button>
          {/* Date picker */}
          <DatePicker
            onChange={(date) => {
              setCurrentDate(new Date(date))
            }}
            allowClear={false}
            picker={'date'}
            value={dayjs(currentDate)}
            style={{ borderRadius: 0, borderRight: 'none', borderLeft: 'none' }}
          />
          {/* Next button */}
          <Button
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            onClick={() => {
              handleDateChange('next')
            }}
          >
            <FontAwesomeIcon icon={faCaretRight} />
          </Button>
        </Space>
        {currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW ? undefined : (
          <Button
            onClick={() => setIsListView(!isListView)}
            style={{ background: isListView ? token.actionColor : '', color: isListView ? '#fff' : '#000' }}
          >
            List View
          </Button>
        )}
        {authState.agiliteUser?.extraData.role.type === 'admin' && (
          <Select
            options={[
              { label: 'All Facilities', value: '' },
              ...facilities.map((facility) => ({
                label: facility.name,
                value: facility._id
              }))
            ]}
            value={facilityFilter}
            onChange={(value) => setFacilityFilter(value)}
            style={{ borderRadius: '6px', width: '250px' }}
            disabled={appointmentsLoading || medicalProfessionalsLoading}
            placeholder='Select Facility'
          />
        )}
        <Tooltip title='Refresh'>
          <Button onClick={() => fetchAppointments()} style={{ background: token.actionColor, color: '#fff' }}>
            <FontAwesomeIcon icon={faRefresh} />
          </Button>
        </Tooltip>
      </Space>
      <CustomLoadingIcon
        loading={appointmentsLoading || medicalProfessionalsLoading}
        content={
          <div>
            <FullCalendar
              slotLabelContent={handleSlotLabelContent}
              displayEventTime={false}
              dateClick={
                currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW
                  ? (info) => {
                      setCurrentView(FullCalendarEnums.viewTypeKeys.DAY_VIEW)
                      setCurrentDate(dayjs(info.date).toDate())
                    }
                  : handleEmptySlotClick
              }
              allDaySlot={false}
              key={`${currentView}-${dayjs(currentDate).format('YYYY-MM-DD')}-${isListView}-${timeFilterActive}-${
                timeFilterActive ? timeRangeFilter.start + timeRangeFilter.end : 'full'
              }`}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView={isListView ? getCurrentListView() : currentView}
              initialDate={currentDate}
              events={currentView === FullCalendarEnums.viewTypeKeys.MONTH_VIEW ? getMonthViewEvents() : events}
              eventTextColor='#000'
              weekends={true}
              eventMouseEnter={handleEventMouseEnter}
              eventMouseLeave={handleEventMouseLeave}
              editable={currentView !== FullCalendarEnums.viewTypeKeys.MONTH_VIEW}
              slotDuration='00:15:00'
              slotLabelInterval='00:15'
              slotMinTime={timeFilterActive && timeRangeFilter ? timeRangeFilter.start + ':00' : '00:00:00'}
              slotMaxTime={timeFilterActive && timeRangeFilter ? timeRangeFilter.end + ':00' : '24:00:00'}
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                omitZeroMinute: false,
                meridiem: false,
                hour12: false
              }}
              height={'calc(100vh - 205px)'}
              headerToolbar={false}
              eventDrop={handleUpdateEvent}
              eventResize={handleUpdateEvent}
              eventClick={handleEventClick}
              scrollTime={timeFilterActive && timeRangeFilter ? timeRangeFilter.start + ':00' : '14:00:00'}
              listDaySideFormat={false}
              firstDay={currentDate.getDay()}
              dayHeaderContent={DayHeaderContent}
              {...extendedProps}
            />
          </div>
        }
      />
      <FullCalendarContentModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        event={modalData}
        setModalData={setModalData}
        selectedProfessional={selectedProfessional}
        refreshFunction={fetchAppointments}
        medicalProfessionals={sortedMedicalProfessionals}
      />
    </ContainerCard>
  )
}

export default EHRCalendar
